/** @jsxImportSource @emotion/react */
import { OutputData } from "@editorjs/editorjs";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Backdrop,
  css,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TablePagination,
  Tooltip,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useEffect, useState, VFC } from "react";
import { useTranslation } from "react-i18next";
import {
  // AiOutlineDownload,
  AiOutlineCopy,
  AiOutlineEye,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlinePlusCircle,
} from "react-icons/ai";
import { NavigateFunction, useNavigate } from "react-router";
import { CountryPath } from "src/entities/CountryPath";
import { ArticlesResponse, ArticleV3 } from "src/entities/v3/ArticlesResponse";
import { DeleteArticleResponse } from "src/entities/v3/DeleteArticleResponse";
import { GetArticleResponse } from "src/entities/v3/GetArticleResponse";
import { useDelete } from "src/hooks/useDelete";
import { useFetch } from "src/hooks/useFetch";
import getCountryName from "src/utils/getCountryName";
import ArticlePreview from "../components/article/ArticlePreview";
// import DownloadPreview from "../components/article/DownloadPreview";
import Button from "../components/common/Button";
import DuplicateArticleModal from "../components/modal/DuplicateArticleModal";
import PageContentsWrapper from "../components/page/PageContentsWrapper";
import PageHeader from "../components/page/PageHeader";
import PageWrapper from "../components/page/PageWrapper";
import StyledTable from "../components/table/StyledTable";
import StyledTableBody from "../components/table/StyledTableBody";
import StyledTableCell from "../components/table/StyledTableCell";
import StyledTableHeader from "../components/table/StyledTableHeader";
import StyledTableHeaderRow from "../components/table/StyledTableHeaderRow";
import StyledTableRow from "../components/table/StyledTableRow";

interface Props {
  signout: (navigate: NavigateFunction) => boolean;
  lang: string;
  country: CountryPath;
  setCountry: (country: CountryPath) => void;
  publisherId: string;
  publisherName: string;
  isPublisherLoading: boolean;
}

/**
 * Sort by
 */
type SortBy = "title" | "published_at" | "status" | "updated_at";
/**
 * Sort order
 */
type SortOrder = "asc" | "desc";

const DashboardPage: VFC<Props> = (props: Props) => {
  const navigate = useNavigate();

  const [t, i18n] = useTranslation();
  const [open, setOpen] = useState(false);
  // const [downloadOpen, setDownloadOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [previewedSlug, setPreviewedSlug] = useState<string>("");
  const [selectedSlug, setSelectedSlug] = useState<string>("");
  const [sortBy, setSortBy] = useState<SortBy>("published_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const { enqueueSnackbar } = useSnackbar();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [articleToDuplicate, setArticleToDuplicate] = useState<string>("");
  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // const handleClickDownloadOpen = () => {
  //   setDownloadOpen(true);
  // };

  // const handleDownloadClose = () => {
  //   setDownloadOpen(false);
  // };

  const handleClickDeleteOpen = () => {
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
  };

  const handleBackDropOpen = () => {
    setBackdropOpen(true);
  };

  const handleBackdropClose = () => {
    setBackdropOpen(false);
  };

  const onClickPreview = (slug: string) => {
    setPreviewedSlug(slug);
    handleClickOpen();
  };

  // const onClickDownload = (slug: string) => {
  //   setPreviewedSlug(slug);
  //   handleClickDownloadOpen();
  // };

  useEffect(() => {
    if (props.lang) {
      i18n.changeLanguage(props.lang);
    } else {
      i18n.changeLanguage("en");
    }
  }, [i18n, props.lang]);

  useEffect(() => {
    if (props.publisherId) {
      refetchArticles();
    }
  }, [props.country]);

  const {
    data: articlesData,
    isLoading: isArticlesLoading,
    refetch: refetchArticles,
  } = useFetch<ArticlesResponse>({
    url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/articles?country=${
      props.country
    }&limit=${rowsPerPage}&offset=${page * rowsPerPage}`,
    headers: {
      country: props.country,
    },
    skip: props.publisherId === undefined,
    onError: () => {
      enqueueSnackbar(t("error.login_again"), {
        variant: "error",
      });
      props.signout(navigate);
    },
  });

  const sortedArticle =
    articlesData &&
    articlesData.data &&
    sortArticles(articlesData?.data, sortBy, sortOrder);

  const { exeDelete } = useDelete<DeleteArticleResponse>({
    url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/articles/${selectedSlug}`,
    headers: {},
    onSuccess: () => {
      handleDeleteClose();
      refetchArticles();
      enqueueSnackbar(t("dashboard.delete_succeeded"), {
        variant: "success",
      });
    },
    onError: () => {
      enqueueSnackbar(t("dashboard.delete_failed"), {
        variant: "error",
      });
    },
    onStart: handleBackDropOpen,
    onEnd: () => {
      handleDeleteClose();
      handleBackdropClose();
    },
  });

  const { data: previewedArticleRaw } = useFetch<GetArticleResponse>({
    url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/articles/${selectedSlug}`,
    headers: {
      country: props.country,
    },
    skip: !selectedSlug,
    onError: () => {
      enqueueSnackbar(t("error.login_again"), {
        variant: "error",
      });
      props.signout(navigate);
    },
  });

  // Parse body from JSON string to OutputData
  const previewedArticle = previewedArticleRaw
    ? {
        ...previewedArticleRaw,
        body: JSON.parse(previewedArticleRaw.body) as OutputData,
      }
    : undefined;

  return (
    <PageWrapper>
      <Backdrop sx={{ color: "#fff", zIndex: 10001 }} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          onClick={() => {
            onClickPreview(selectedSlug);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <AiOutlineEye size="24px" />
          </ListItemIcon>
          <ListItemText sx={{ minWidth: 80 }}>
            {t("dashboard.table.actions.preview")}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate(`/article/${props.publisherId}/${selectedSlug}/CREATE`);
            handleMenuClose();
          }}
          disabled={
            articlesData?.data?.find((article) => article.id === selectedSlug)
              ?.state === "suspended"
          }
        >
          <ListItemIcon>
            <AiOutlineEdit size="24px" />
          </ListItemIcon>
          <ListItemText sx={{ minWidth: 80 }}>
            {t("dashboard.table.actions.edit")}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setArticleToDuplicate(selectedSlug);
            setDuplicateModalOpen(true);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <AiOutlineCopy size="24px" />
          </ListItemIcon>
          <ListItemText sx={{ minWidth: 80 }}>
            {t("dashboard.table.actions.duplicate")}
          </ListItemText>
        </MenuItem>
        {/* {props.country !== "THA" && (
          <MenuItem
            onClick={() => {
              onClickDownload(selectedSlug);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <AiOutlineDownload size="24px" />
            </ListItemIcon>
            <ListItemText sx={{ minWidth: 80 }}>
              {t("dashboard.table.actions.download")}
            </ListItemText>
          </MenuItem>
        )} */}
        <MenuItem
          onClick={() => {
            handleClickDeleteOpen();
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <AiOutlineDelete size="24px" />
          </ListItemIcon>
          <ListItemText sx={{ minWidth: 80 }}>
            {t("dashboard.table.actions.delete")}
          </ListItemText>
        </MenuItem>
      </Menu>
      <Dialog
        open={deleteOpen}
        onClose={handleDeleteClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xs"
        fullWidth
      >
        <div className="p-4">
          <div style={{ textAlign: "right" }}>
            <IconButton onClick={handleDeleteClose}>
              <CloseIcon />
            </IconButton>
          </div>
          <DialogContent>
            <div className="text-base text-black pt-4 pb-4">
              {t("dashboard.delete_press_release")}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              text={t("dashboard.modal.button.cancel")}
              onClick={handleDeleteClose}
            />
            <Button
              text={t("dashboard.modal.button.delete")}
              onClick={() => {
                exeDelete({});
              }}
            />
          </DialogActions>
        </div>
      </Dialog>
      {previewedArticle && previewedSlug ? (
        <ArticlePreview
          open={open}
          handleClose={handleClose}
          title={previewedArticle.title}
          body={previewedArticle.body}
          summary={previewedArticle.summary}
          use_thumbnail_as_featured_image={
            previewedArticle.useThumbnailAsFeaturedImage
          }
          thumbnail_url={previewedArticle.thumbnailImageUrl}
          company_info={previewedArticle.companyInfo}
          media_contact={previewedArticle.mediaContact}
          publisher_name={props.publisherName}
        />
      ) : null}
      <DuplicateArticleModal
        open={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        articleId={articleToDuplicate}
        currentCountry={props.country}
        setCountry={props.setCountry}
      />
      {/* {publisherInfo && previewedSlug ? (
        <DownloadPreview
          open={downloadOpen}
          handleClose={handleDownloadClose}
          publisherInfo={publisherInfo}
          article={publisherInfo.articles.find(
            (article) => article.slug === previewedSlug
          )}
        />
      ) : null} */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PageHeader
          title={t("dashboard.page_header", {
            country: getCountryName(props.country),
          })}
        />
        <div className="flex flex-wrap items-center gap-2">
          {/* New: direct entry to AI article generation */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                navigate(`/article/new/ai?country=${props.country}`);
              }}
              className="inline-flex items-center gap-1.5 rounded-md border border-primary bg-white px-4 py-2 text-base font-semibold text-primary transition-colors hover:border-button-hover hover:text-button-hover"
            >
              <AutoAwesomeIcon fontSize="small" />
              {t("dashboard.create_with_ai")}
              <span className="ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-primary">
                {t("create_press_release.select_method.ai.beta")}
              </span>
            </button>
            <span className="absolute -right-2 -top-2 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white shadow">
              {t("dashboard.ai_new_badge")}
            </span>
          </div>
          <Button
            text={t("dashboard.create_press_release")}
            onClick={() => {
              navigate(`/article/new`);
            }}
          />
        </div>
      </div>
      <PageContentsWrapper>
        {props.isPublisherLoading || isArticlesLoading ? (
          <div className="flex h-full w-full justify-center">
            <CircularProgress />
          </div>
        ) : articlesData && articlesData.data?.length > 0 ? (
          <StyledTable>
            <StyledTableHeader>
              <StyledTableHeaderRow>
                <th className="px-4 w-5% border-r" align="center">
                  {" "}
                </th>
                <th
                  onClick={() => {
                    setSortBy("title");
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  }}
                  className="px-4 w-40% border-r hover:cursor-pointer"
                  align="left"
                >
                  {t("dashboard.table.title")}
                  {sortBy === "title" ? (
                    sortOrder === "asc" ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    )
                  ) : null}
                </th>
                <th
                  onClick={() => {
                    setSortBy("status");
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  }}
                  className="px-4 w-15% border-r hover:cursor-pointer"
                  align="left"
                >
                  {t("dashboard.table.state")}
                  {sortBy === "status" ? (
                    sortOrder === "asc" ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    )
                  ) : null}
                </th>
                <th
                  onClick={() => {
                    setSortBy("published_at");
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  }}
                  className="px-4 w-20% border-r hover:cursor-pointer"
                  align="left"
                >
                  {t("dashboard.table.post_date")}
                  {sortBy === "published_at" ? (
                    sortOrder === "asc" ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    )
                  ) : null}
                </th>
                <th
                  onClick={() => {
                    setSortBy("updated_at");
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                  }}
                  className="px-4 w-20% border-r hover:cursor-pointer"
                  align="left"
                >
                  {t("dashboard.table.update_date")}
                  {sortBy === "updated_at" ? (
                    sortOrder === "asc" ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    )
                  ) : null}
                </th>
              </StyledTableHeaderRow>
            </StyledTableHeader>
            <StyledTableBody>
              {sortedArticle?.map((article) => {
                return (
                  <StyledTableRow key={article.id}>
                    <StyledTableCell button>
                      <IconButton
                        onClick={(e) => {
                          setSelectedSlug(article.id);
                          handleMenuClick(e);
                        }}
                        id="menu-button"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </StyledTableCell>
                    <StyledTableCell>
                      <p className="truncate">{article.title}</p>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <div className="truncate flex justify-center items-center">
                        {article.state === "published" && (
                          <>
                            <div className="w-2 h-2 mr-2 rounded-2xl bg-green-400">
                              {" "}
                            </div>
                            {t("dashboard.table.state.published")}
                          </>
                        )}
                        {article.state === "draft" && (
                          <>
                            <div className="w-2 h-2 mr-2 rounded-2xl bg-yellow-500">
                              {" "}
                            </div>
                            {t("dashboard.table.state.draft")}
                          </>
                        )}
                        {article.state === "scheduled" && (
                          <>
                            <div className="w-2 h-2 mr-2 rounded-2xl bg-sky-500">
                              {" "}
                            </div>
                            {t("dashboard.table.state.scheduled")}
                          </>
                        )}
                        {article.state === "suspended" && (
                          <Tooltip title={t("dashboard.table.suspended.error")}>
                            <div className="flex items-center">
                              <div className="w-2 h-2 mr-2 rounded-2xl bg-red-500">
                                {" "}
                              </div>
                              {t("dashboard.table.state.suspended")}
                            </div>
                          </Tooltip>
                        )}
                        {article.state === "payment_failed" && (
                          <Tooltip title={t("dashboard.table.failed.error")}>
                            <div className="flex items-center">
                              <div className="w-2 h-2 mr-2 rounded-2xl bg-red-500">
                                {" "}
                              </div>
                              {t("dashboard.table.state.failed")}
                            </div>
                          </Tooltip>
                        )}
                      </div>
                    </StyledTableCell>
                    <StyledTableCell>
                      <p style={{ fontSize: 14 }}>
                        {DateTime.fromISO(article.publishedAt).toFormat(
                          "MMM dd yyyy h:mm a"
                        )}
                      </p>
                    </StyledTableCell>
                    <StyledTableCell>
                      <p style={{ fontSize: 14 }}>
                        {article.updatedAt
                          ? DateTime.fromISO(article.updatedAt).toFormat(
                              "MMM dd yyyy h:mm a"
                            )
                          : null}
                      </p>
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })}
            </StyledTableBody>
            {/* <StyledTableFooter /> */}
          </StyledTable>
        ) : null}
        {articlesData && articlesData.data && articlesData.data.length > 0 && (
          <TablePagination
            component="div"
            count={articlesData.total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
        {articlesData &&
          (!articlesData.data || articlesData.data.length === 0) && (
            <button
              type="button"
              onClick={() => {
                navigate("/article/new");
              }}
              css={addDescriptionStyle}
            >
              <div className="mb-2 font-semibold">
                {t("dashboard.add_press_release")}
              </div>
              <AiOutlinePlusCircle size="48px" />
            </button>
          )}
      </PageContentsWrapper>
    </PageWrapper>
  );
};

function sortArticles(
  articles: ArticleV3[],
  sortBy: SortBy,
  order: SortOrder
): ArticleV3[] {
  if (sortBy === "published_at") {
    if (order === "asc") {
      return articles.sort((a, b) => {
        return (
          DateTime.fromISO(a.publishedAt).toMillis() -
          DateTime.fromISO(b.publishedAt).toMillis()
        );
      });
    }
    if (order === "desc") {
      return articles.sort((a, b) => {
        return (
          DateTime.fromISO(b.publishedAt).toMillis() -
          DateTime.fromISO(a.publishedAt).toMillis()
        );
      });
    }
  } else if (sortBy === "updated_at") {
    if (order === "asc") {
      return articles.sort((a, b) => {
        return (
          DateTime.fromISO(a.updatedAt).toMillis() -
          DateTime.fromISO(b.updatedAt).toMillis()
        );
      });
    }
    if (order === "desc") {
      return articles.sort((a, b) => {
        return (
          DateTime.fromISO(b.updatedAt).toMillis() -
          DateTime.fromISO(a.updatedAt).toMillis()
        );
      });
    }
  } else if (sortBy === "title") {
    if (order === "asc") {
      return articles.sort((a, b) => {
        return a.title.localeCompare(b.title);
      });
    }
    if (order === "desc") {
      return articles.sort((a, b) => {
        return b.title.localeCompare(a.title);
      });
    }
  } else if (sortBy === "status") {
    if (order === "asc") {
      return articles.sort((a, b) => {
        return a.state.localeCompare(b.state);
      });
    }
    if (order === "desc") {
      return articles.sort((a, b) => {
        return b.state.localeCompare(a.state);
      });
    }
  }
  return articles;
}

const addDescriptionStyle = css`
  flex: 1;
  width: 100%;
  height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
  border-width: 2px;
  border-radius: 8px;
  border-color: #eeeeee;
  border-style: dashed;
  background-color: #fafafa;
  color: #bdbdbd;
  outline: none;
  transition: border 0.24s ease-in-out;
  cursor: pointer;
  font-size: 18px;
  :hover {
    color: #808080;
    border-color: #808080;
  }
`;

export default DashboardPage;
